import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  FunnelSimple,
  CircleNotch,
  Phone,
  Envelope,
  ChatCircle,
  Globe,
  UserPlus,
  Eye,
  Trash,
  ArrowRight,
  Copy,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Play,
  Pause,
  FileText,
  CaretDown,
  CaretUp,
  SpeakerHigh,
  Microphone
} from '@phosphor-icons/react';


const SOURCE_CONFIG = {
  retell: { label: 'Retell (Llamada)', icon: Phone, color: 'bg-purple-100 text-purple-700' },
  chatbot: { label: 'Chatbot Web', icon: ChatCircle, color: 'bg-blue-100 text-blue-700' },
  email: { label: 'Email Entrante', icon: Envelope, color: 'bg-amber-100 text-amber-700' },
  whatsapp: { label: 'WhatsApp', icon: ChatCircle, color: 'bg-green-100 text-green-700' },
  manual: { label: 'Manual', icon: UserPlus, color: 'bg-slate-100 text-slate-700' },
  website: { label: 'Sitio Web', icon: Globe, color: 'bg-cyan-100 text-cyan-700' },
  website_quote: { label: 'Cotizacion Web', icon: Globe, color: 'bg-teal-100 text-teal-700' },
  b2b_prospecting: { label: 'B2B Prospecting', icon: Globe, color: 'bg-indigo-100 text-indigo-700' },
  inbound_email: { label: 'Email Entrante', icon: Envelope, color: 'bg-amber-100 text-amber-700' }
};

const STATUS_CONFIG = {
  'New': { label: 'Nuevo', color: 'bg-blue-100 text-blue-700' },
  'Contacted': { label: 'Contactado', color: 'bg-yellow-100 text-yellow-700' },
  'Qualified': { label: 'Calificado', color: 'bg-indigo-100 text-indigo-700' },
  'Converted': { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  'Lost': { label: 'Perdido', color: 'bg-red-100 text-red-700' },
};

function SourceBadge({ source }) {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.manual;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['New'];
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}
          data-testid={`lead-status-${status}`}>
      {config.label}
    </span>
  );
}

function TranscriptViewer({ transcript, notes }) {
  const [expanded, setExpanded] = useState(false);

  // Extract transcript from notes if not a top-level field
  let displayText = transcript || '';
  if (!displayText && notes) {
    const idx = notes.indexOf('Transcripcion:\n');
    if (idx !== -1) {
      displayText = notes.substring(idx + 'Transcripcion:\n'.length).trim();
    } else {
      const idxAlt = notes.indexOf('Resumen:');
      if (idxAlt !== -1) {
        displayText = notes.substring(idxAlt).trim();
      }
    }
  }

  if (!displayText) return null;

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="retell-transcript-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
        data-testid="transcript-toggle"
      >
        <div className="flex items-center gap-2">
          <Microphone size={16} className="text-indigo-600" />
          <p className="text-xs font-semibold text-indigo-700">Transcripcion de Llamada</p>
        </div>
        {expanded ? <CaretUp size={14} className="text-indigo-500" /> : <CaretDown size={14} className="text-indigo-500" />}
      </button>
      {expanded && (
        <div className="mt-2 max-h-64 overflow-y-auto">
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="transcript-content">
            {displayText}
          </p>
        </div>
      )}
    </div>
  );
}

function EmailTimeline({ leadId }) {
  const [emails, setEmails] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchEmails = async () => {
    if (emails.length > 0) {
      setExpanded(!expanded);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/email-logs?related_to_id=${leadId}`,
        { withCredentials: true }
      );
      setEmails(response.data);
      setExpanded(true);
    } catch (err) {
      console.error('Error fetching email logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const typeLabels = {
    welcome: 'Bienvenida',
    follow_up: 'Seguimiento',
    notification: 'Notificacion Admin',
    quote: 'Cotizacion',
    payment_link: 'Link de Pago',
    gc_report: 'Reporte GC',
  };

  const typeColors = {
    welcome: 'bg-green-100 text-green-700',
    follow_up: 'bg-amber-100 text-amber-700',
    notification: 'bg-blue-100 text-blue-700',
    quote: 'bg-purple-100 text-purple-700',
    payment_link: 'bg-cyan-100 text-cyan-700',
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="email-timeline-section">
      <button
        onClick={fetchEmails}
        className="flex items-center justify-between w-full text-left"
        data-testid="email-timeline-toggle"
      >
        <div className="flex items-center gap-2">
          <Envelope size={16} className="text-slate-600" />
          <p className="text-xs font-semibold text-slate-700">
            Emails Enviados {emails.length > 0 ? `(${emails.length})` : ''}
          </p>
        </div>
        {loading ? (
          <CircleNotch size={14} className="animate-spin text-slate-400" />
        ) : expanded ? (
          <CaretUp size={14} className="text-slate-500" />
        ) : (
          <CaretDown size={14} className="text-slate-500" />
        )}
      </button>
      {expanded && emails.length > 0 && (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
          {emails.map((em) => (
            <div key={em.id} className="flex items-start gap-2 p-2 bg-white rounded border border-slate-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${typeColors[em.email_type] || 'bg-slate-100 text-slate-600'}`}>
                    {typeLabels[em.email_type] || em.email_type}
                  </span>
                  {em.status === 'failed' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-600">Fallido</span>
                  )}
                </div>
                <p className="text-xs text-slate-700 truncate">{em.subject}</p>
                <p className="text-[10px] text-slate-400">
                  {em.to} &middot; {new Date(em.sent_at).toLocaleString('es-ES')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      {expanded && emails.length === 0 && !loading && (
        <p className="text-xs text-slate-400 mt-2">No hay emails registrados para este lead</p>
      )}
    </div>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  const [copied, setCopied] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
  }, [statusFilter, sourceFilter]);

  // Auto-refresh leads every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLeads();
    }, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, sourceFilter]);

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API_URL}/api/leads${qs}`, { withCredentials: true });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/retell/sync-status`, { withCredentials: true });
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleRetellSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/retell/sync`, {}, { withCredentials: true });
      alert(`${response.data.message}`);
      fetchLeads();
      fetchSyncStatus();
    } catch (error) {
      console.error('Error syncing Retell:', error);
      alert('Error al sincronizar con Retell: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/leads/${leadId}/status`, { status: newStatus }, { withCredentials: true });
      fetchLeads();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleConvert = async (leadId) => {
    try {
      const response = await axios.post(`${API_URL}/api/leads/${leadId}/convert`, {}, { withCredentials: true });
      alert('Lead convertido a cliente exitosamente');
      navigate(`/clients/${response.data.client_id}`);
    } catch (error) {
      console.error('Error converting lead:', error);
      alert(error.response?.data?.detail || 'Error al convertir lead');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Eliminar este lead?')) return;
    try {
      await axios.delete(`${API_URL}/api/leads/${leadId}`, { withCredentials: true });
      fetchLeads();
      if (selectedLead?.id === leadId) setSelectedLead(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} leads seleccionados?`)) return;
    try {
      await axios.post(`${API_URL}/api/bulk/delete-leads`, { ids: [...selectedIds] }, { withCredentials: true });
      setSelectedIds(new Set());
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      console.error('Error bulk delete:', error);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredLeads.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredLeads.map(l => l.id)));
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleAutoQuote = async (leadId) => {
    if (!window.confirm('¿Generar cotizacion automatica desde este lead?')) return;
    try {
      const response = await axios.post(`${API_URL}/api/leads/${leadId}/auto-quote`, {}, { withCredentials: true });
      alert(`${response.data.message}\nTotal: $${response.data.total}\nItems: ${response.data.items_count}`);
      fetchLeads();
      navigate(`/quotes/${response.data.quote_id}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar cotizacion');
    }
  };

  const handleFollowUp = async (leadId) => {
    if (!window.confirm('¿Enviar email de seguimiento a este lead?')) return;
    try {
      const response = await axios.post(`${API_URL}/api/leads/${leadId}/follow-up`, {}, { withCredentials: true });
      alert(response.data.message);
      fetchLeads();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, follow_up_count: response.data.follow_up_count, status: 'Contacted' });
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al enviar seguimiento');
    }
  };

  const webhookBaseUrl = API_URL;

  const filteredLeads = leads;
  const countByStatus = (status) => leads.filter(l => l.status === status).length;
  const countBySource = (source) => leads.filter(l => l.source === source).length;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-slate-500">Leads capturados desde Retell, Chatbot, Email y WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRetellSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
            data-testid="retell-sync-button"
          >
            {syncing ? (
              <CircleNotch size={20} className="animate-spin" />
            ) : (
              <ArrowsClockwise size={20} />
            )}
            {syncing ? 'Sincronizando...' : 'Sync Retell'}
          </button>
          <button
            onClick={() => setShowWebhookInfo(!showWebhookInfo)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            data-testid="webhook-info-button"
          >
            <Globe size={20} />
            Webhooks
          </button>
        </div>
      </div>

      {/* Webhook Info Panel */}
      {showWebhookInfo && (
        <div className="bg-slate-900 rounded-xl p-6 text-white" data-testid="webhook-info-panel">
          <h3 className="font-bold text-lg mb-4">URLs de Webhooks para Integracion</h3>
          <p className="text-slate-400 text-sm mb-4">Configura estos URLs en cada servicio para capturar leads automaticamente:</p>
          <div className="space-y-3">
            {[
              { label: 'Retell AI', url: `${webhookBaseUrl}/api/webhooks/retell`, desc: 'Configura en Retell Dashboard > Agent > Webhook URL' },
              { label: 'Chatbot', url: `${webhookBaseUrl}/api/webhooks/chatbot`, desc: 'Agrega en tu proyecto de Emergent (iqeslowvoltage.com)' },
              { label: 'Email Entrante', url: `${webhookBaseUrl}/api/webhooks/inbound-email`, desc: 'Configura reenvio de emails o Resend Inbound' },
              { label: 'WhatsApp', url: `${webhookBaseUrl}/api/webhooks/whatsapp`, desc: 'Para integracion futura con WhatsApp Business API' },
            ].map((wh) => (
              <div key={wh.label} className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{wh.label}</span>
                  <button
                    onClick={() => copyToClipboard(wh.url, wh.label)}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                    data-testid={`copy-webhook-${wh.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {copied === wh.label ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                    {copied === wh.label ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
                <code className="text-xs text-green-400 block mb-1 break-all">{wh.url}</code>
                <p className="text-xs text-slate-500">{wh.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retell Sync Status */}
      {syncStatus && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between" data-testid="retell-sync-status">
          <div className="flex items-center gap-3">
            <Phone size={24} className="text-purple-600" />
            <div>
              <p className="font-medium text-purple-900">Retell AI Sync</p>
              <p className="text-sm text-purple-600">
                {syncStatus.total_calls_synced} llamadas sincronizadas
                {syncStatus.last_sync && (
                  <> | Ultima sync: {new Date(syncStatus.last_sync).toLocaleString('es-ES')}</>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {syncStatus.configured ? (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">API Key Configurada</span>
            ) : (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">API Key No Configurada</span>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="leads-total">{leads.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Nuevos</p>
          <p className="text-2xl font-bold text-blue-600">{countByStatus('New')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Contactados</p>
          <p className="text-2xl font-bold text-yellow-600">{countByStatus('Contacted')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Convertidos</p>
          <p className="text-2xl font-bold text-green-600">{countByStatus('Converted')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Perdidos</p>
          <p className="text-2xl font-bold text-red-600">{countByStatus('Lost')}</p>
        </div>
      </div>

      {/* Source stats row */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(SOURCE_CONFIG).map(([key, config]) => {
          const count = countBySource(key);
          if (count === 0) return null;
          const Icon = config.icon;
          return (
            <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${config.color}`}>
              <Icon size={14} />
              {config.label}: {count}
            </span>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <FunnelSimple size={20} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            data-testid="lead-status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="New">Nuevo</option>
            <option value="Contacted">Contactado</option>
            <option value="Qualified">Calificado</option>
            <option value="Converted">Convertido</option>
            <option value="Lost">Perdido</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            data-testid="lead-source-filter"
          >
            <option value="">Todas las fuentes</option>
            <option value="retell">Retell (Llamada)</option>
            <option value="chatbot">Chatbot</option>
            <option value="website_quote">Cotizacion Web</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="b2b_prospecting">B2B Prospecting</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div data-testid="bulk-delete-bar" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-red-700 font-medium text-sm">{selectedIds.size} leads seleccionados</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm text-slate-600 hover:text-slate-800">Deseleccionar</button>
            <button data-testid="bulk-delete-btn" onClick={handleBulkDelete} className="bg-red-600 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-red-700">Eliminar Seleccionados</button>
          </div>
        </div>
      )}

      {/* Leads List + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <CircleNotch size={32} className="animate-spin text-blue-600" />
            </div>
          ) : leads.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {/* Select All */}
              <div className="px-4 py-2 bg-slate-50 flex items-center gap-2">
                <input type="checkbox" checked={selectedIds.size === filteredLeads.length && filteredLeads.length > 0} onChange={toggleSelectAll} className="w-4 h-4 accent-blue-600" />
                <span className="text-xs text-slate-500">Seleccionar todos</span>
              </div>
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => setSelectedLead(lead)}
                  className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${selectedLead?.id === lead.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <input type="checkbox" checked={selectedIds.has(lead.id)} onChange={(e) => { e.stopPropagation(); toggleSelect(lead.id); }} onClick={(e) => e.stopPropagation()} className="w-4 h-4 mt-1 accent-blue-600" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-900 truncate">{lead.name}</p>
                          <StatusBadge status={lead.status} />
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {lead.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={12} />
                              {lead.phone}
                            </span>
                          )}
                          {lead.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Envelope size={12} />
                              {lead.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <SourceBadge source={lead.source} />
                      <span className="text-xs text-slate-400">
                        {new Date(lead.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </div>
                  {lead.service_interest && (
                    <p className="text-xs text-slate-500 mt-1">Interes: {lead.service_interest}</p>
                  )}
                  {lead.lead_score !== undefined && lead.lead_score !== null && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${lead.lead_score >= 70 ? 'bg-green-500' : lead.lead_score >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`}
                          style={{ width: `${lead.lead_score}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold ${lead.lead_score >= 70 ? 'text-green-600' : lead.lead_score >= 40 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {lead.lead_score}
                      </span>
                    </div>
                  )}
                  {lead.assigned_to_name && (
                    <p className="text-xs text-blue-600 mt-1">Asignado: {lead.assigned_to_name}</p>
                  )}
                  {lead.source === 'retell' && (lead.audio_url || lead.raw_data?.recording_url) && (
                    <span className="inline-flex items-center gap-1 text-xs text-purple-600 mt-1">
                      <SpeakerHigh size={12} /> Audio disponible
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <UserPlus size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-2">No hay leads todavia</p>
              <p className="text-sm text-slate-400">Los leads llegaran automaticamente cuando configures los webhooks</p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          {selectedLead ? (
            <div className="space-y-4" data-testid="lead-detail-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg text-slate-900">{selectedLead.name}</h3>
                <StatusBadge status={selectedLead.status} />
              </div>

              <SourceBadge source={selectedLead.source} />

              {/* Lead Score */}
              {selectedLead.lead_score !== undefined && selectedLead.lead_score !== null && (
                <div className="bg-slate-50 rounded-lg p-3" data-testid="lead-score-display">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-500">Lead Score</p>
                    <span className={`text-lg font-bold ${selectedLead.lead_score >= 70 ? 'text-green-600' : selectedLead.lead_score >= 40 ? 'text-yellow-600' : 'text-red-500'}`}>
                      {selectedLead.lead_score}/100
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${selectedLead.lead_score >= 70 ? 'bg-green-500' : selectedLead.lead_score >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`}
                      style={{ width: `${selectedLead.lead_score}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {selectedLead.lead_score >= 70 ? 'Alta probabilidad' : selectedLead.lead_score >= 40 ? 'Probabilidad media' : 'Baja probabilidad'}
                  </p>
                </div>
              )}

              {/* Follow-up info */}
              {selectedLead.follow_up_count > 0 && (
                <p className="text-xs text-slate-400">
                  Follow-ups enviados: {selectedLead.follow_up_count}
                  {selectedLead.last_follow_up_at && (
                    <> | Ultimo: {new Date(selectedLead.last_follow_up_at).toLocaleDateString('es-ES')}</>
                  )}
                </p>
              )}

              {/* Assigned to */}
              {selectedLead.assigned_to_name && (
                <div className="bg-blue-50 rounded-lg p-2 flex items-center gap-2">
                  <UserPlus size={14} className="text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">Asignado a: {selectedLead.assigned_to_name}</span>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {selectedLead.phone && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <Phone size={16} className="text-slate-400" />
                    <a href={`tel:${selectedLead.phone}`} className="text-blue-600 hover:underline">{selectedLead.phone}</a>
                  </p>
                )}
                {selectedLead.email && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <Envelope size={16} className="text-slate-400" />
                    <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">{selectedLead.email}</a>
                  </p>
                )}
                {selectedLead.service_interest && (
                  <p className="text-slate-600">
                    <span className="font-medium">Interes:</span> {selectedLead.service_interest}
                  </p>
                )}
                <p className="text-slate-400 text-xs">
                  Creado: {new Date(selectedLead.created_at).toLocaleString('es-ES')}
                </p>
              </div>

              {/* Email Timeline */}
              <EmailTimeline leadId={selectedLead.id} />

              {/* Retell Audio Player */}
              {(selectedLead.audio_url || selectedLead.raw_data?.recording_url || selectedLead.raw_data?.audio_url) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid="retell-audio-section">
                  <div className="flex items-center gap-2 mb-2">
                    <SpeakerHigh size={16} className="text-purple-600" />
                    <p className="text-xs font-semibold text-purple-700">Grabacion de Llamada</p>
                  </div>
                  <audio
                    controls
                    className="w-full h-8"
                    src={selectedLead.audio_url || selectedLead.raw_data?.recording_url || selectedLead.raw_data?.audio_url}
                    data-testid="retell-audio-player"
                  >
                    Tu navegador no soporta audio.
                  </audio>
                  <a
                    href={selectedLead.audio_url || selectedLead.raw_data?.recording_url || selectedLead.raw_data?.audio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-purple-600 hover:underline mt-1 inline-block"
                    data-testid="retell-audio-download"
                  >
                    Descargar audio
                  </a>
                </div>
              )}

              {/* Retell Transcript */}
              {(selectedLead.transcript || (selectedLead.notes && selectedLead.source === 'retell' && selectedLead.notes.includes('Transcripcion:'))) && (
                <TranscriptViewer
                  transcript={selectedLead.transcript}
                  notes={selectedLead.notes}
                />
              )}

              {selectedLead.notes && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-500 mb-1">Notas</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedLead.notes}
                  </p>
                </div>
              )}

              {/* Status actions */}
              <div className="space-y-2 pt-2 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-500">Cambiar Estado:</p>
                <div className="flex flex-wrap gap-1.5">
                  {['New', 'Contacted', 'Qualified', 'Lost'].map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(selectedLead.id, s)}
                      disabled={selectedLead.status === s}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-40 ${
                        selectedLead.status === s ? 'bg-slate-200 text-slate-500' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`change-status-${s.toLowerCase()}`}
                    >
                      {STATUS_CONFIG[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex gap-2">
                  {!selectedLead.converted && selectedLead.status !== 'Lost' && (
                    <button
                      onClick={() => handleConvert(selectedLead.id)}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      data-testid="convert-lead-button"
                    >
                      <ArrowRight size={16} />
                      Convertir a Cliente
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(selectedLead.id)}
                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid="delete-lead-button"
                  >
                    <Trash size={18} />
                  </button>
                </div>

                {/* Auto-Quote button */}
                {!selectedLead.auto_quote_id && selectedLead.status !== 'Lost' && (
                  <button
                    onClick={() => handleAutoQuote(selectedLead.id)}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    data-testid="auto-quote-button"
                  >
                    <Globe size={16} />
                    Generar Cotizacion Auto
                  </button>
                )}

                {selectedLead.auto_quote_id && (
                  <button
                    onClick={() => navigate(`/quotes/${selectedLead.auto_quote_id}`)}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                    data-testid="view-auto-quote-button"
                  >
                    <Eye size={16} />
                    Ver Cotizacion ({selectedLead.auto_quote_number})
                  </button>
                )}

                {/* Follow-up button */}
                {selectedLead.email && selectedLead.status !== 'Converted' && selectedLead.status !== 'Lost' && (
                  <button
                    onClick={() => handleFollowUp(selectedLead.id)}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium border border-amber-200"
                    data-testid="follow-up-button"
                  >
                    <Envelope size={16} />
                    Enviar Follow-up {selectedLead.follow_up_count > 0 ? `(#${selectedLead.follow_up_count + 1})` : ''}
                  </button>
                )}
              </div>

              {selectedLead.converted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle size={16} />
                    Convertido a cliente
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Eye size={40} className="mb-3" />
              <p className="text-sm">Selecciona un lead para ver detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
